import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { enrichRouteMeta } from "./utils/enrichRouteMeta";
import Error from "@/views/Error.vue";
import PartnerPermissions from "@/views/portal/PartnerUnauthorized.vue";
import RepareoReportDatePicker from "@/views/report/repareo/RepareoFormDatePicker.vue";
import RepareoHome from "@/views/report/repareo/RepareoHome.vue";
import RepareoSuccess from "@/views/report/repareo/RepareoSuccess.vue";
import RepareoSummary from "@/views/report/repareo/RepareoSummary.vue";
import ReportAccidentDetails from "@/views/report/ReportAccidentDetails.vue";
import ReportAdditionalImage from "@/views/report/ReportAdditionalImage.vue";
import ReportAdditonalInformationHueffermann from "@/views/report/ReportAdditonalInformationHueffermann.vue";
import ReportCompletionImage from "@/views/report/ReportCompletionImage.vue";
import ReportContact from "@/views/report/ReportContact.vue";
import ReportCurrentLocation from "@/views/report/ReportCurrentLocation.vue";
import ReportDamageNotice from "@/views/report/ReportDamageNotice.vue";
import ReportErrorPage from "@/views/report/ReportErrorPage.vue";
import ReportDatePicker from "@/views/report/ReportFormDatePicker.vue";
import ReportDescription from "@/views/report/ReportFormDescription.vue";
import ReportDescriptionRequired from "@/views/report/ReportFormDescriptionRequired.vue";
import ReportPersonalData from "@/views/report/ReportFormPersonalData.vue";
import ReportFormPersonalDataAlt from "@/views/report/ReportFormPersonalDataAlt.vue";
import ReportPersonalDataSimple from "@/views/report/ReportFormPersonalDataSimple.vue";
import ReportCockpitImage from "@/views/report/ReportImageCockpit.vue";
import ReportDamageImage from "@/views/report/ReportImageDamage.vue";
import ReportFrontTruckImage from "@/views/report/ReportFrontTruckImage.vue";
import ReportGlassDamageImage from "@/views/report/ReportGlassDamageImage.vue";
import ReportDamageDetailImage from "@/views/report/ReportImageDamageDetail.vue";
import ReportDamageDetailImageRequired from "@/views/report/ReportImageDamageDetailRequired.vue";
import ReportImageDamageOptional from "@/views/report/ReportImageDamageOptional.vue";
import ReportImageDriversLicense from "@/views/report/ReportImageDriversLicense.vue";
import ReportOverviewImage from "@/views/report/ReportImageOverview.vue";
import ReportImageOverviewAlt from "@/views/report/ReportImageOverviewAlt.vue";
import ReportImageOverviewAuth from "@/views/report/ReportImageOverviewAuth.vue";
import ReportRegistrationImage from "@/views/report/ReportImageRegistration.vue";
import ReportImageTire from "@/views/report/ReportImageTire.vue";
import ReportImageTuev from "@/views/report/ReportImageTuev.vue";
import ReportImageVin from "@/views/report/ReportImageVin.vue";
import ReportImageWheel from "@/views/report/ReportImageWheel.vue";
import ReportImageWheelDetail from "@/views/report/ReportImageWheelDetail.vue";
import ReportInsurance from "@/views/report/ReportInsurance.vue";
import ReportLeasing from "@/views/report/ReportLeasing.vue";
import ReportLiabilityDamage from "@/views/report/ReportLiabilityDamage.vue";
import ReportLocation from "@/views/report/ReportLocation.vue";
import ReportLocationCombined from "@/views/report/ReportLocationCombined.vue";
import ReportLocationTruck from "@/views/report/ReportLocationTruck.vue";
import ReportMotoristLegalInsurance from "@/views/report/ReportMotoristLegalInsurance.vue";
import ReportNumberplate from "@/views/report/ReportNumberplate.vue";
import ReportPolice from "@/views/report/ReportPolice.vue";
import ReportDocumentUpload from "@/views/report/ReportDocumentUpload.vue";
import ReportPreExistingDamage from "@/views/report/ReportPreExistingDamage.vue";
import ReportSuccess from "@/views/report/ReportSuccess.vue";
import ReportSummary from "@/views/report/ReportSummary.vue";
import ReportSummaryAlt from "@/views/report/ReportSummaryAlt.vue";
import ReportSummaryAltAuth from "@/views/report/ReportSummaryAltAuth.vue";
import ReportSummaryAuth from "@/views/report/ReportSummaryAuth.vue";
import ReportSummarySimple from "@/views/report/ReportSummarySimple.vue";
import ReportTripDetails from "@/views/report/ReportTripDetails.vue";
import ReportTypeSelector from "@/views/report/ReportTypeSelector.vue";
import ReportWheelInsurance from "@/views/report/ReportWheelInsurance.vue";
import ReportWitness from "@/views/report/ReportWitness.vue";
import ReportWitnessWithNumberplate from "@/views/report/ReportWitnessWithNumberplate.vue";
import ReportWrapping from "@/views/report/ReportWrapping.vue";
import ReportControlClosing from "@/views/ReportControlClosing.vue";
import ReportHome from "@/views/ReportHome.vue";
import ReportPartnerFinder from "@/views/ReportPartnerFinder.vue";

import NoRoles from "@/views/NoRoles.vue";
import Login from "@/views/report/Login.vue";
import SignUp from "@/views/report/SignUp.vue";
import UserProfile from "@/views/UserProfile.vue";

import FrequentlyAskedQuestions from "@/views/report/FrequentlyAskedQuestions.vue";

import { authGuard } from "@/auth/authGuard";
import { permissionGuard } from "@/auth/permissionAuthGuard";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import ReportPersonalDataSimpleNoMail from "@/views/report/ReportFormPersonalDataSimpleNoMail.vue";
import { BaseRoutes } from "./base.router";
import { BookingRoutes } from "./bookingApp.router";
import { getLoginRoutes } from "./login.router";
import { SettingRoutes } from "./settings.router";
import ReportImageCockpitDetail from "@/views/report/ReportImageCockpitDetail.vue";

const SharedContentView = () => import("@/views/report/SharedContentView.vue");
const ReportDataResponse = () => import("@/views/report/ReportDataResponse.vue");
const ReportDataResponseConfirm = () => import("@/views/report/ReportDataResponseConfirm.vue");

const Sign = () => import("@/views/report/Sign.vue");

const MyBookingView = () => import(/* webpackChunkName: "report-user" */ "@/views/report/user/MyBookingView.vue");
const MyDamageView = () => import(/* webpackChunkName: "report-user" */ "@/views/report/user/MyDamageView.vue");
const MySignatureView = () => import(/* webpackChunkName: "report-user" */ "@/views/report/user/MySignatureView.vue");

const FleetDamageView = () => import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetDamageView.vue");
const FleetVehicleView = () =>
  import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetVehicleView.vue");
const FleetInvoiceView = () =>
  import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetInvoiceView.vue");
const FleetEventView = () => import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetEventView.vue");
const FleetContractView = () =>
  import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetContractView.vue");
const FleetHomeView = () => import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetHomeView.vue");
const FleetAnalyticsView = () =>
  import(/* webpackChunkName: "report-fleet" */ "@/views/report/fleet/FleetAnalyticsView.vue");

Vue.use(VueRouter);

export const routes: Array<RouteConfig> = [
  {
    path: "/" + ReportScreenEnum.welcome,
    component: ReportHome,
    name: "ReportHome"
  },
  {
    path: "/" + ReportScreenEnum.welcomelogin,
    component: ReportHome,
    name: "ReportHomeLogin",
    beforeEnter: authGuard
  },
  {
    path: "/s/:token",
    component: SharedContentView,
    name: "ReportSharedContentView"
  },
  {
    name: "ReportDataResponse",
    path: "/r/:accessToken",
    component: ReportDataResponse
  },
  {
    path: "/request/:attachmentId/:accessToken",
    component: ReportDataResponse,
    name: "ReportDataResponseAttachmentId"
  },
  {
    path: "/attach",
    component: ReportDataResponseConfirm,
    name: "ReportDataResponseConfirm"
  },
  {
    path: "/" + ReportScreenEnum.registration,
    component: ReportRegistrationImage,
    name: "ReportRegistrationImage"
  },
  {
    path: "/" + ReportScreenEnum.damagelocator,
    component: ReportLocation,
    name: "ReportLocation"
  },
  {
    path: "/" + ReportScreenEnum.damagelocatortruck,
    component: ReportLocationTruck,
    name: "ReportLocationTruck"
  },
  {
    path: "/" + ReportScreenEnum.damagelocatorcombined,
    component: ReportLocationCombined,
    name: "ReportLocationCombined"
  },

  {
    path: "/" + ReportScreenEnum.overviewimage,
    component: ReportOverviewImage,
    name: "ReportOverviewImage"
  },
  {
    path: "/" + ReportScreenEnum.overviewimagealt,
    component: ReportImageOverviewAlt,
    name: "ReportImageOverviewAlt"
  },
  {
    path: "/" + ReportScreenEnum.overviewimageauth,
    component: ReportImageOverviewAuth,
    beforeEnter: authGuard,
    name: "ReportImageOverviewAuth"
  },
  {
    path: "/" + ReportScreenEnum.componentimage,
    component: ReportDamageImage,
    name: "ReportDamageImage"
  },
  {
    path: "/" + ReportScreenEnum.fronttruck,
    component: ReportFrontTruckImage,
    name: "ReportFrontTruckImage"
  },
  {
    path: "/" + ReportScreenEnum.glassdamageimage,
    component: ReportGlassDamageImage,
    name: "ReportGlassDamageImage"
  },
  {
    path: "/" + ReportScreenEnum.damagedetail,
    component: ReportDamageDetailImage,
    name: "ReportDamageDetailImage"
  },

  {
    path: "/" + ReportScreenEnum.damagedetailrequired,
    component: ReportDamageDetailImageRequired,
    name: "ReportDamageDetailImageRequired"
  },
  {
    path: "/" + ReportScreenEnum.damageimageoptional,
    component: ReportImageDamageOptional,
    name: "ReportImageDamageOptional"
  },
  {
    path: "/" + ReportScreenEnum.mileage,
    component: ReportCockpitImage,
    name: "ReportCockpitImage"
  },
  {
    path: "/" + "mileageDetail",
    component: ReportImageCockpitDetail,
    name: "ReportImageCockpitDetail"
  },
  {
    path: "/" + ReportScreenEnum.message,
    component: ReportDescription,
    name: "ReportDescription"
  },
  {
    path: "/" + ReportScreenEnum.messagerequired,
    component: ReportDescriptionRequired,
    name: "ReportDescriptionRequired"
  },
  {
    path: "/" + ReportScreenEnum.contactdetails,
    component: ReportPersonalData,
    name: "ReportPersonalData"
  },
  {
    path: "/" + ReportScreenEnum.contactalt,
    component: ReportFormPersonalDataAlt,
    name: "ReportFormPersonalDataAlt"
  },
  {
    path: "/" + ReportScreenEnum.simplecontact,
    component: ReportPersonalDataSimple,
    name: "ReportPersonalDataSimple"
  },
  {
    path: "/" + ReportScreenEnum.simplecontactnoemail,
    component: ReportPersonalDataSimpleNoMail,
    name: "ReportPersonalDataSimpleNoMail"
  },
  {
    path: "/" + ReportScreenEnum.daterequest,
    component: ReportDatePicker,
    name: "ReportDatePicker"
  },
  {
    path: "/" + ReportScreenEnum.closing,
    component: ReportSummary,
    name: "ReportSummary"
  },
  {
    path: "/" + ReportScreenEnum.closingalt,
    component: ReportSummaryAlt,
    name: "ReportSummaryAlt"
  },
  {
    path: "/" + ReportScreenEnum.closingaltauth,
    component: ReportSummaryAltAuth,
    beforeEnter: authGuard,
    name: "ReportSummaryAltAuth"
  },
  {
    path: "/" + ReportScreenEnum.closingauth,
    component: ReportSummaryAuth,
    beforeEnter: authGuard,
    name: "ReportSummaryAuth"
  },
  {
    path: "/" + ReportScreenEnum.closingsimple,
    component: ReportSummarySimple,
    name: "ReportSummarySimple"
  },
  {
    path: "/" + ReportScreenEnum.reportType,
    component: ReportTypeSelector,
    name: "ReportType"
  },
  {
    path: "/" + ReportScreenEnum.vinimage,
    component: ReportImageVin,
    name: "ReportImageVin"
  },
  {
    path: "/" + ReportScreenEnum.tuevimage,
    component: ReportImageTuev,
    name: "ReportImageTuev"
  },
  {
    path: "/" + ReportScreenEnum.tireimage,
    component: ReportImageTire,
    name: "ReportImageTire"
  },
  {
    path: "/" + ReportScreenEnum.wheelImage,
    component: ReportImageWheel,
    name: "ReportImageWheel"
  },
  {
    path: "/" + ReportScreenEnum.wheelDetailImage,
    component: ReportImageWheelDetail,
    name: "ReportImageWheelDetail"
  },
  {
    path: "/" + ReportScreenEnum.wheelInsurance,
    component: ReportWheelInsurance,
    name: "ReportWheelInsurance"
  },
  {
    path: "/" + ReportScreenEnum.witness,
    component: ReportWitness,
    name: "ReportWitness"
  },
  {
    path: "/" + ReportScreenEnum.witnessNumberplate,
    component: ReportWitnessWithNumberplate,
    name: "ReportWitnessWithNumberplate"
  },
  {
    path: "/" + ReportScreenEnum.police,
    component: ReportPolice,
    name: "ReportPolice"
  },
  {
    path: "/" + ReportScreenEnum.wrapping,
    component: ReportWrapping,
    name: "ReportWrapping"
  },
  {
    path: "/" + ReportScreenEnum.driverslicense,
    component: ReportImageDriversLicense,
    name: "ReportImageDriversLicense"
  },
  {
    path: "/" + ReportScreenEnum.damagenotice,
    component: ReportDamageNotice,
    name: "ReportDamageNotice"
  },
  {
    path: "/" + ReportScreenEnum.liabilityselector,
    component: ReportLiabilityDamage,
    name: "ReportLiabilityDamage"
  },
  {
    path: "/" + ReportScreenEnum.accidentdetails,
    component: ReportAccidentDetails,
    name: "ReportAccidentDetails"
  },
  {
    path: "/" + ReportScreenEnum.externalidhueffermann,
    component: ReportAdditonalInformationHueffermann,
    name: "ReportAdditonalInformationHueffermann"
  },
  {
    path: "/" + ReportScreenEnum.additionalimage,
    component: ReportAdditionalImage,
    name: "ReportAdditionalImage"
  },
  {
    path: "/" + ReportScreenEnum.numberplate,
    component: ReportNumberplate,
    name: "ReportNumberplate"
  },
  {
    path: "/" + ReportScreenEnum.leasing,
    component: ReportLeasing,
    name: "ReportLeasing"
  },
  {
    path: "/" + ReportScreenEnum.preexistingdamage,
    component: ReportPreExistingDamage,
    name: "ReportPreExistingDamage"
  },
  {
    path: "/" + ReportScreenEnum.documentupload,
    component: ReportDocumentUpload,
    name: "ReportDocumentUpload"
  },
  {
    path: "/" + ReportScreenEnum.insurance,
    component: ReportInsurance,
    name: "ReportInsurance"
  },
  {
    path: "/" + ReportScreenEnum.motoristLegalInsurance,
    component: ReportMotoristLegalInsurance,
    name: "ReportMotoristLegalInsurance"
  },
  {
    path: "/" + ReportScreenEnum.CONTACT,
    component: ReportContact,
    name: "ReportContact"
  },
  {
    path: "/" + ReportScreenEnum.COMPLETION,
    component: ReportCompletionImage,
    name: "ReportCompletionImage"
  },
  {
    path: "/success",
    component: ReportSuccess,
    name: "ReportSuccess"
  },

  /**
   * Reparo Screens
   */
  {
    path: "/" + ReportScreenEnum.REPAREO_HOME,
    component: RepareoHome
  },
  {
    path: "/" + ReportScreenEnum.REPAREO_DATE_REQUEST,
    component: RepareoReportDatePicker
  },
  {
    path: "/" + ReportScreenEnum.REPAREO_CLOSING,
    component: RepareoSummary
  },
  {
    path: "/repareosuccess",
    component: RepareoSuccess,
    name: "RepareoSuccess"
  },
  /**
   * BVDP
   */
  {
    path: "/" + ReportScreenEnum.PARTNER_FINDER,
    component: ReportPartnerFinder,
    name: "ReportPartnerFinder"
  },
  {
    path: "/" + ReportScreenEnum.REPORT_CONTROL_CLOSING,
    component: ReportControlClosing,
    name: "ReportControlClosing"
  },
  {
    path: "/" + ReportScreenEnum.tripDetails,
    component: ReportTripDetails,
    name: "ReportTripDetails"
  },
  {
    path: "/" + ReportScreenEnum.currentLocation,
    component: ReportCurrentLocation,
    name: "ReportCurrentLocation"
  },
  {
    path: "/my/damages",
    name: "MyDamageView",
    component: MyDamageView,
    permission: {
      resource: ResourceEnum.LOGGED_IN_USER,
      action: ActionEnum.READ
    }
  },
  {
    path: "/my/bookings",
    name: "MyBookingView",
    component: MyBookingView,
    permission: {
      resource: ResourceEnum.LOGGED_IN_USER,
      action: ActionEnum.READ
    }
  },
  {
    path: "/my/signatures",
    name: "MySignatureView",
    component: MySignatureView,
    permission: {
      resource: ResourceEnum.LOGGED_IN_USER,
      action: ActionEnum.READ
    }
  },
  {
    path: "/fleet",
    name: "FleetHomeView",
    component: FleetHomeView
  },
  {
    path: "/fleet/analytics",
    name: "FleetAnalyticsView",
    component: FleetAnalyticsView
  },
  {
    path: "/fleet/damages",
    name: "FleetDamageView",
    component: FleetDamageView
  },
  {
    path: "/fleet/vehicles",
    name: "FleetVehicleView",
    component: FleetVehicleView
  },
  {
    path: "/fleet/contracts",
    name: "FleetContractView",
    component: FleetContractView
  },
  {
    path: "/fleet/invoices",
    name: "FleetInvoiceView",
    component: FleetInvoiceView
  },
  {
    path: "/fleet/events",
    name: "FleetEventView",
    component: FleetEventView
  }
];

const appRoutes: Array<RouteConfig> = [
  {
    path: "/error",
    name: "PartnerContactError",
    component: ReportErrorPage
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: PartnerPermissions
  },
  {
    path: "/no-roles",
    name: "NoRoles",
    component: NoRoles
  },
  {
    path: "/errors",
    name: "Error",
    component: Error
  },
  {
    path: "/sign/:signRequestId",
    name: "DigitalSignature",
    component: Sign
  },
  {
    path: "/faq",
    name: "FrequentlyAskedQuestions",
    component: FrequentlyAskedQuestions
  },
  {
    path: "*",
    component: ReportHome
  }
];

SettingRoutes.forEach((route: RouteConfig) => {
  routes.push(route);
});

getLoginRoutes(Login).forEach((route: RouteConfig) => {
  route.path = "/login" + route.path;
  routes.push(route);
});

routes.push(
  {
    path: "/sign-up",
    name: "SignUp",
    component: SignUp
  },
  {
    path: "/profile",
    name: "Profile",
    component: UserProfile,
    beforeEnter: authGuard
  }
);

const router = new VueRouter({
  routes: enrichRouteMeta([...routes, ...BookingRoutes, ...BaseRoutes, ...appRoutes]),
  base: "/report/",
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

router.beforeEach(permissionGuard);

export default router;
